import { ENGINE_FACT_MARKER } from "@/lib/seo/engine-fact-marker";

export const CONTENT_QUALITY_FACETS = [
  "primary_task",
  "direct_answer",
  "calculation_basis",
  "explanation",
  "evidence",
  "limitations",
  "who",
  "next_step",
  "how",
  "why",
  "corrections",
  "local_context",
] as const;

export type ContentQualityFacet = (typeof CONTENT_QUALITY_FACETS)[number];

export const CONTENT_QUALITY_VALUES = [
  "engine",
  "original_analysis",
  "first_hand_experience",
  "sourced_interpretation",
  "original_dataset",
  "methodology",
] as const;

export type ContentQualityValue = (typeof CONTENT_QUALITY_VALUES)[number];

export const CONTENT_QUALITY_EVIDENCE_IDENTIFIERS = [
  "canonical-engine",
  "first-hand",
  "original-dataset:",
  "methodology:",
  "source:https://",
] as const;

export type ContentQualityEvidenceSource =
  | "canonical-engine"
  | "first-hand"
  | `original-dataset:${string}`
  | `methodology:${string}`
  | `source:https://${string}`;

export interface ContentQualitySignal {
  facet: ContentQualityFacet;
  value?: ContentQualityValue;
  evidenceSource?: ContentQualityEvidenceSource;
  engineFacts?: string;
}

/**
 * Typed projection of rule 66's rendered content-quality vocabulary.
 *
 * Keep the page contract on the visible element that proves it. The monitor
 * ignores empty/hidden markers, so this helper cannot make prose or a loading
 * placeholder pass merely by existing in source.
 */
export function contentQualityAttributes(signal: ContentQualitySignal): Record<string, string> {
  const hasEngineFacts = Boolean(signal.engineFacts?.trim());
  if ((signal.value === "engine") !== hasEngineFacts) {
    throw new Error("Engine content-quality value and engine facts must be declared together");
  }

  const attributes: Record<string, string> = {
    "data-content-facet": signal.facet,
  };
  if (signal.value) attributes["data-content-value"] = signal.value;
  if (signal.evidenceSource) attributes["data-evidence-source"] = signal.evidenceSource;
  if (signal.engineFacts) attributes[ENGINE_FACT_MARKER] = signal.engineFacts;
  return attributes;
}
